import { useState } from 'react';

export default function ContactSection() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get('phone_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      return;
    }

    setFormStatus('loading');
    setFormError('');

    try {
      const response = await fetch('https://readdy.ai/api/form/d9re6i7nahb7382ovla0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(
          Array.from(formData.entries()).filter(([k]) => k !== 'phone_alt').map(([k, v]) => [k, v.toString()])
        ).toString(),
      });

      const responseText = await response.text();
      let parsed: Record<string, unknown> | null = null;
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      if (response.ok && parsed && parsed.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg: string = (parsed && typeof (parsed as Record<string, unknown>).meta === 'object' && typeof ((parsed as Record<string, unknown>).meta as Record<string, unknown>)?.message === 'string')
          ? ((parsed as Record<string, unknown>).meta as Record<string, string>).message
          : (parsed && typeof (parsed as Record<string, unknown>).message === 'string')
            ? (parsed as Record<string, string>).message
            : responseText || '提交失败，请稍后再试';
        setFormError(serverMsg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormStatus('error');
    }
  };

  return (
    <section id="contact" className="w-full py-16 md:py-20 bg-background-50">
      <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <span className="inline-block text-xs font-medium tracking-widest uppercase text-secondary-600 bg-secondary-100/60 px-4 py-1.5 rounded-full mb-4">
            联系我们
          </span>
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
            我们期待与您相遇
          </h2>
          <p className="text-sm text-foreground-600 max-w-lg mx-auto">
            无论您有任何疑问、建议或合作意向，都欢迎与我们联系。久久伊人团队将在24小时内回复您。
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-8 md:gap-10">
          <div className="md:col-span-2 space-y-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 shrink-0">
                <i className="ri-mail-line text-lg text-primary-600" />
              </div>
              <div>
                <h4 className="font-heading text-sm font-semibold text-foreground-900 mb-1">电子邮箱</h4>
                <a href="mailto:hello@jiujiuyiren.com" className="text-sm text-foreground-600 hover:text-primary-600 transition-colors duration-200 cursor-pointer">
                  hello@jiujiuyiren.com
                </a>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 shrink-0">
                <i className="ri-phone-line text-lg text-primary-600" />
              </div>
              <div>
                <h4 className="font-heading text-sm font-semibold text-foreground-900 mb-1">客服热线</h4>
                <a href="tel:400-888-9999" className="text-sm text-foreground-600 hover:text-primary-600 transition-colors duration-200 cursor-pointer">
                  400-888-9999
                </a>
                <p className="text-xs text-foreground-400 mt-1">周一至周五 9:00-18:00</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 shrink-0">
                <i className="ri-map-pin-line text-lg text-primary-600" />
              </div>
              <div>
                <h4 className="font-heading text-sm font-semibold text-foreground-900 mb-1">公司地址</h4>
                <p className="text-sm text-foreground-600">上海市静安区南京西路1788号久久大厦</p>
              </div>
            </div>
          </div>

          <div className="md:col-span-3">
            <form
              data-readdy-form
              onSubmit={handleSubmit}
              className="bg-background-100 rounded-lg p-6 md:p-8 border border-background-200/70"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    姓名 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    id="contact-name"
                    name="name"
                    type="text"
                    required
                    className="w-full px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 transition-all duration-200"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    邮箱 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    id="contact-email"
                    name="email"
                    type="email"
                    required
                    className="w-full px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 transition-all duration-200"
                    placeholder="请输入您的邮箱"
                  />
                </div>
              </div>
              <div className="mb-4">
                <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  主题
                </label>
                <input
                  id="contact-subject"
                  name="subject"
                  type="text"
                  className="w-full px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 transition-all duration-200"
                  placeholder="请输入消息主题"
                />
              </div>
              <div className="mb-5">
                <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  留言 <span className="text-primary-500">*</span>
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  required
                  maxLength={500}
                  rows={4}
                  className="w-full px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 transition-all duration-200 resize-none"
                  placeholder="请描述您的需求或问题（最多500字）"
                />
                <p className="text-xs text-foreground-400 mt-1">最多可输入500个字符</p>
              </div>

              <input
                type="text"
                name="phone_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="form-honeypot"
              />

              {formStatus === 'error' && formError && (
                <div className="mb-4 p-3 rounded-md bg-primary-50 border border-primary-200 text-sm text-primary-700">
                  <i className="ri-error-warning-line mr-1.5" />
                  {formError}
                </div>
              )}

              {formStatus === 'success' && (
                <div className="mb-4 p-3 rounded-md bg-accent-50 border border-accent-200 text-sm text-accent-700">
                  <i className="ri-check-line mr-1.5" />
                  感谢您的留言！我们将在24小时内回复您。
                </div>
              )}

              <button
                type="submit"
                disabled={formStatus === 'loading'}
                className="w-full py-3 rounded-md bg-primary-500 text-background-50 font-medium text-sm whitespace-nowrap cursor-pointer transition-all duration-300 hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {formStatus === 'loading' ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin" />
                    提交中...
                  </span>
                ) : (
                  '发送留言'
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}